import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/ReduxHooks';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { setInputBindingList } from '../../../redux/EditorSlice';
import type { InputBinding, InputBindingDevice } from '../../../utils/inputBindings';
import { INPUT_BINDING_DEVICE } from '../../../utils/inputBindings';
import { editor as MonacoEditor } from 'monaco-editor';
import { useResolvedMappingsReady } from '../../../hooks/editor/usePythonTreeSitterReady';
import { saveButtonBindings, TREE_SITTER_NOT_READY_REASON, type SaveButtonBindingsResult } from './controllerCodeUtils';

const { DRIVEBASE } = INPUT_BINDING_DEVICE;

export function useMappingUi(device: InputBindingDevice, editor?: MonacoEditor.IStandaloneCodeEditor, mappingsReady?: boolean) {
  const dispatch = useAppDispatch();
  const bindingList = useAppSelector((state) => state.editor.inputBindingList);
  const bindingListRef = useRef<InputBinding[]>(bindingList);
  const hoveredGlobalButton = useAppSelector((state) => state.application.hoveredControllerButton);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogButton, setDialogButton] = useState<string>(device === DRIVEBASE ? 'up' : 'a');
  const readOnly = useAppSelector((state) => state.project.loadedProject?.readOnly) ?? false;
  const ready = useResolvedMappingsReady(mappingsReady);

  const mappingsByButton = useMemo(() => {
    const byButton: Record<string, InputBinding[]> = {};
    for (const item of bindingList) {
      if (item.device !== device) continue;
      const btn = String(item.button || '').toLowerCase();
      if (!byButton[btn]) byButton[btn] = [];
      byButton[btn].push(item);
    }
    return byButton;
  }, [bindingList, device]);

  const hasMapping = useCallback((btn: string) => (mappingsByButton[btn]?.length ?? 0) > 0, [mappingsByButton]);

  const getButtonFromEvent = (evt: React.MouseEvent<SVGSVGElement>) => {
    const element = evt.target as Element | null;
    const host = element && 'closest' in element ? (element as Element).closest('[data-button]') : null;
    const btn = host ? host.getAttribute('data-button') : null;
    return btn as string | null;
  };

  const handleSvgMouseMove = (evt: React.MouseEvent<SVGSVGElement>) => {
    const btn = getButtonFromEvent(evt);
    if (btn !== hoveredButton) {
      setHoveredButton(btn);
      dispatch(applicationSlice.actions.onHoverControllerButton(btn));
    }
  };

  const handleSvgMouseLeave = () => {
    if (hoveredButton !== null) {
      setHoveredButton(null);
      dispatch(applicationSlice.actions.onHoverControllerButton(null));
    }
  };

  const openDialogForButton = (btn: string) => {
    // Tree-sitter owns mutations; keep Cancel/browse available but do not open a save dialog
    // while WASM is still loading or after init permanently failed.
    if (!ready) return;
    setDialogButton(btn);
    setDialogOpen(true);
  };

  const handleSvgClick = (evt: React.MouseEvent<SVGSVGElement>) => {
    const btn = getButtonFromEvent(evt);
    if (btn && !readOnly) openDialogForButton(btn);
  };

  const handleSaveButtonBindings = (button: string, nextBindings: InputBinding[]): SaveButtonBindingsResult => {
    if (!ready) return { ok: false, reason: TREE_SITTER_NOT_READY_REASON.replace };
    const currentBindingList = bindingListRef.current;
    const result = saveButtonBindings(editor, currentBindingList, device, button, nextBindings);
    // Failures are returned to ButtonMappingDialog, which shows saveError — do not also logger.error.
    if (!result.ok) return result;
    dispatch(setInputBindingList(result.nextBindingList));
    return result;
  };

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const nodes = svg.querySelectorAll('[data-button]');
    nodes.forEach((node) => {
      node.classList.add('controller-button');
      const btn = node.getAttribute('data-button') || '';
      const mapped = hasMapping(btn);
      const hovered = btn === hoveredButton || btn === hoveredGlobalButton;
      node.classList.toggle('mapped', mapped);
      node.classList.toggle('hovered', hovered);
    });
  }, [mappingsByButton, hoveredButton, hoveredGlobalButton, hasMapping]);

  useEffect(() => {
    bindingListRef.current = bindingList;
  }, [bindingList]);

  return {
    svgRef,
    readOnly,
    mappingsReady: ready,
    hoveredButton,
    dialogOpen,
    dialogButton,
    setDialogOpen,
    handleSvgMouseMove,
    handleSvgMouseLeave,
    handleSvgClick,
    handleSaveButtonBindings,
  } as const;
}
